import React, { useState, useEffect, useRef } from 'react';
import Odontograma from './Odontograma';
import TermAutocomplete from '../../comun/TermAutocomplete';

import api from '../../services/api';
import { savePendingTransaction } from '../../servicios/offlineSync';
import Modal from '../../comun/Modal';
import ConfirmModal from '../../comun/ConfirmModal';

const Consultas = () => {
    const [quotes, setQuotes] = useState([]);
    const [searchQuote, setSearchQuote] = useState('');
    const [currentQuote, setCurrentQuote] = useState(null);
    const [saveModal, setSaveModal] = useState({ show: false, message: '', type: 'online' });
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
    const [duplicateQuoteAlert, setDuplicateQuoteAlert] = useState({ show: false, existingQuote: null, pendingSaveData: null });

    const defaultForm = {
        nombre: '',
        cedula: '',
        representante: '',
        cedulaRepresentante: '',
        fechaNacimiento: '',
        direccion: '',
        telefono: '',
        correo: '',
        ocupacion: '',
        inversion: '',
        discountPercentage: 0
    };

    const [form, setForm] = useState(defaultForm);
    const [odontogramaData, setOdontogramaData] = useState({});
    const [treatments, setTreatments] = useState([{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    const [servicios, setServicios] = useState([]);

    const loadQuotes = async (targetPatientId, targetQuoteId) => {
        try {
            const resRecords = await api.get('/api/clinical-records/?limit=1000');
            const records = resRecords.data.results || resRecords.data;
            const quoteRecords = records.filter(r => 
                r.reason_for_consultation === 'Cotización / Presupuesto' || 
                r.reason_for_consultation === 'Cotización / Presupuesto (Completado)'
            );
            
            const resPatients = await api.get('/api/patients/?limit=1000');
            const patients = resPatients.data.results || resPatients.data;
            const patientMap = {};
            patients.forEach(p => patientMap[p.id] = p);

            const formattedQuotes = quoteRecords.map(r => {
                const p = patientMap[r.patient] || {};
                const quoteDate = new Date(r.updated_at || r.created_at);
                const savedDiscount = r.diagnostic_plans?.discountPercentage !== undefined 
                    ? r.diagnostic_plans.discountPercentage 
                    : (r.discountPercentage || 0);
                const savedInversion = r.diagnostic_plans?.inversion !== undefined 
                    ? r.diagnostic_plans.inversion 
                    : (r.inversion || '');

                return {
                    id: r.id,
                    db_patient_id: p.id,
                    status: r.reason_for_consultation.includes('Completado') ? 'Completada' : 'Borrador',
                    date: quoteDate.toLocaleDateString(),
                    rawDate: quoteDate,
                    form: {
                        nombre: `${p.first_name || ''} ${p.last_name || ''}`.trim().toUpperCase(),
                        cedula: p.cedula || '',
                        representante: (p.representative || '').toUpperCase(),
                        cedulaRepresentante: p.representative_cedula || '',
                        fechaNacimiento: p.birth_date || '',
                        direccion: p.address || '',
                        telefono: p.phone || '',
                        correo: p.email || '',
                        ocupacion: p.occupation || '',
                        inversion: savedInversion,
                        discountPercentage: savedDiscount
                    },
                    odontogramaData: r.odontogram_data || {},
                    treatments: r.treatments ? r.treatments.map(t => ({...t, total_final: t.total_final !== undefined ? t.total_final : ((parseFloat(t.precio)||0)*(parseInt(t.cantidad)||1))})) : [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]
                };
            });
            formattedQuotes.sort((a, b) => b.rawDate - a.rawDate);
            setQuotes(formattedQuotes);

            // Auto-selección si viene paciente o cotización por parámetro URL
            if (targetQuoteId) {
                const found = formattedQuotes.find(q => String(q.id) === String(targetQuoteId));
                if (found) {
                    handleSelectQuote(found);
                    return;
                }
            }
            if (targetPatientId) {
                const found = formattedQuotes.find(q => String(q.db_patient_id) === String(targetPatientId));
                if (found) {
                    handleSelectQuote(found);
                    return;
                } else if (patientMap[targetPatientId]) {
                    // El paciente existe pero aún no tiene cotización guardada: cargamos sus datos y su odontograma clínico
                    const p = patientMap[targetPatientId];
                    const patientClinicalRecords = records.filter(r => String(r.patient) === String(targetPatientId) || (r.patient && String(r.patient.id) === String(targetPatientId)));
                    const mainRecord = patientClinicalRecords.find(r => !r.reason_for_consultation?.includes('Cotización / Presupuesto')) || patientClinicalRecords[0];

                    const prefilledQuote = {
                        id: null,
                        db_patient_id: p.id,
                        status: 'Borrador',
                        date: new Date().toLocaleDateString(),
                        form: {
                            nombre: `${p.first_name || ''} ${p.last_name || ''}`.trim().toUpperCase(),
                            cedula: p.cedula || '',
                            representante: (p.representative || '').toUpperCase(),
                            cedulaRepresentante: p.representative_cedula || '',
                            fechaNacimiento: p.birth_date || '',
                            direccion: p.address || '',
                            telefono: p.phone || '',
                            correo: p.email || '',
                            ocupacion: p.occupation || '',
                            inversion: '',
                            discountPercentage: 0
                        },
                        odontogramaData: mainRecord?.odontogram_data || {},
                        treatments: [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]
                    };
                    setCurrentQuote(prefilledQuote);
                    setForm(prefilledQuote.form);
                    setOdontogramaData(prefilledQuote.odontogramaData);
                    setTreatments(prefilledQuote.treatments);
                }
            }
        } catch (error) {
            console.error('Error cargando cotizaciones desde DB:', error);
        }
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const pId = urlParams.get('patient_id');
        const qId = urlParams.get('quote_id');
        loadQuotes(pId, qId);
        loadServicios();
    }, []);

    const loadServicios = async () => {
        try {
            const res = await api.get('/api/treatments/');
            setServicios(res.data.results || res.data || []);
        } catch (error) {
            console.error('Error cargando servicios:', error);
        }
    };

    const handleNewQuote = () => {
        setCurrentQuote(null);
        setForm(defaultForm);
        setOdontogramaData({});
        setTreatments([{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    };

    const handleSelectQuote = (quote) => {
        setCurrentQuote(quote);
        setForm(quote.form || defaultForm);
        setOdontogramaData(quote.odontogramaData || {});
        setTreatments(quote.treatments ? quote.treatments.map(t => ({...t, total_final: t.total_final !== undefined ? t.total_final : ((parseFloat(t.precio)||0)*(parseInt(t.cantidad)||1))})) : [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    };

    const executeSave = async (patientData, recordData, quoteToUpdateId, patientIdOverride) => {
        const queueLocally = (knownPatientId) => {
            const queued = savePendingTransaction(quoteToUpdateId ? 'UPDATE_QUOTE' : 'NEW_QUOTE', { patientData, recordData, quoteId: quoteToUpdateId, patientId: knownPatientId || null });
            setSaveModal(queued
                ? { show: true, message: 'No hay conexión a internet. Los datos quedaron guardados SOLO en este dispositivo y se enviarán al servidor automáticamente cuando vuelva la conexión.', type: 'offline' }
                : { show: true, message: 'No hay conexión y no se pudo guardar localmente. NO cierre esta ventana; intente guardar de nuevo cuando vuelva la conexión.', type: 'error' });
        };

        if (!navigator.onLine) {
            queueLocally(patientIdOverride);
            return;
        }

        let patientId = patientIdOverride;
        try {
            
            if (!patientId) {
                try {
                    const resPatient = await api.post('/api/patients/', patientData);
                    patientId = resPatient.data.id;
                } catch (err) {
                    if (err.response?.status === 400 && err.response.data?.cedula) {
                        const resSearch = await api.get(`/api/patients/?search=${patientData.cedula}`);
                        const searchResults = resSearch.data.results || resSearch.data || [];
                        if (searchResults.length > 0) {
                            patientId = searchResults[0].id;
                            await api.patch(`/api/patients/${patientId}/`, patientData);
                        } else {
                            throw err;
                        }
                    } else {
                        throw err;
                    }
                }
            } else {
                await api.patch(`/api/patients/${patientId}/`, patientData);
            }

            recordData.patient = patientId;

            if (quoteToUpdateId) {
                await api.patch(`/api/clinical-records/${quoteToUpdateId}/`, recordData);
            } else {
                await api.post('/api/clinical-records/', recordData);
            }

            // Sincronizar el odontograma a la historia clínica activa del paciente (si existe)
            try {
                const resHistorial = await api.get(`/api/clinical-records/?patient=${patientId}&limit=100`);
                const allHist = resHistorial.data.results || resHistorial.data || [];
                const mainRecord = allHist.find(r => 
                    r.id !== quoteToUpdateId && 
                    !r.reason_for_consultation?.includes('Cotización / Presupuesto')
                );
                if (mainRecord && odontogramaData && Object.keys(odontogramaData).length > 0) {
                    await api.patch(`/api/clinical-records/${mainRecord.id}/`, {
                        odontogram_data: odontogramaData
                    });
                }
            } catch (errSync) {
                console.warn('No se pudo sincronizar odontograma con la historia clínica principal:', errSync);
            }

            setSaveModal({ show: true, message: 'Cliente guardado correctamente, guardado en la nube', type: 'online' });
            loadQuotes();
        } catch (error) {
            // Sin respuesta del servidor = se cayó la conexión: guardar localmente (con el paciente si ya se creó)
            if (!error.response) {
                queueLocally(patientId);
                return;
            }
            const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            setSaveModal({ show: true, message: `Error al guardar: Verifique los datos. ${errorMsg}`, type: 'error' });
        }
    };

    const handleSaveCurrent = async () => {
        if (!form.nombre.trim()) {
            setSaveModal({ show: true, message: 'El nombre del paciente es obligatorio para guardar el presupuesto.', type: 'error' });
            return;
        }

        const patientData = {
            first_name: form.nombre.split(' ')[0] || 'Sin nombre',
            last_name: form.nombre.split(' ').slice(1).join(' ') || '',
            cedula: form.cedula || "",
            phone: form.telefono || '',
            address: form.direccion || '',
            email: form.correo || '',
            occupation: form.ocupacion || '',
            representative: form.representante || '',
            representative_cedula: form.cedulaRepresentante || '',
            birth_date: form.fechaNacimiento || null
        };

        const recordData = {
            patient: null, // Will be filled
            reason_for_consultation: 'Cotización / Presupuesto',
            odontogram_data: odontogramaData,
            diagnostic_plans: { 
                discountPercentage: parseFloat(form.discountPercentage) || 0,
                inversion: form.inversion || ''
            },
            treatments: treatments
        };

        if (!currentQuote) {
            const existingQuote = quotes.find(q => 
                (form.cedula && q.form.cedula === form.cedula) || 
                (q.form.nombre.toLowerCase() === form.nombre.toLowerCase())
            );

            if (existingQuote) {
                setDuplicateQuoteAlert({
                    show: true,
                    existingQuote: existingQuote,
                    pendingSaveData: { patientData, recordData }
                });
                return;
            }
        }

        await executeSave(patientData, recordData, currentQuote?.id, currentQuote?.db_patient_id);
    };

    const [recovering, setRecovering] = useState(false);
    const [showAllQuotesModal, setShowAllQuotesModal] = useState(false);
    const [searchAllQuotes, setSearchAllQuotes] = useState('');

    const handleRecoverQuotes = async () => {
        if (!window.confirm("¿Seguro que deseas recuperar cotizaciones antiguas perdidas?")) return;
        setRecovering(true);
        try {
            const resRecords = await api.get('/api/clinical-records/?limit=1000');
            const allRecords = resRecords.data.results || resRecords.data;
            let recoveredCount = 0;
            const recoveredPatients = new Set();
            
            for (const record of allRecords) {
                // If it is NOT a quote currently
                if (record.reason_for_consultation !== 'Cotización / Presupuesto' && 
                    record.reason_for_consultation !== 'Cotización / Presupuesto (Completado)') {
                    
                    // Check if the patient ALREADY HAS A QUOTE in the system
                    const hasQuote = allRecords.some(r => 
                        (r.patient === record.patient || (r.patient && r.patient.id === (record.patient.id || record.patient))) && 
                        (r.reason_for_consultation === 'Cotización / Presupuesto' || r.reason_for_consultation === 'Cotización / Presupuesto (Completado)')
                    );

                    // Get the patient ID properly
                    const patientId = typeof record.patient === 'object' ? record.patient.id : record.patient;

                    if (!hasQuote && !recoveredPatients.has(patientId)) {
                        // Extract procedures from the clinical record to rebuild a quote
                        let quoteTreatments = [];
                        
                        if (record.treatments && record.treatments.length > 0) {
                            for (const t of record.treatments) {
                                if (!t || Object.keys(t).length === 0) continue;
                                
                                if (t.quote_prices && t.quote_prices.length > 0) {
                                    quoteTreatments.push(...t.quote_prices.map(q => ({
                                        procedimiento: q.name,
                                        cantidad: 1,
                                        precio: q.price,
                                        total_final: parseFloat(q.price) - parseFloat(q.discount || 0)
                                    })));
                                } else if (t.procedures) {
                                    quoteTreatments.push({
                                        procedimiento: t.procedures,
                                        cantidad: 1,
                                        precio: 0,
                                        total_final: 0
                                    });
                                } else if (t.service_ids || t.service_id) {
                                    const sIds = Array.isArray(t.service_ids) ? t.service_ids : [t.service_id];
                                    sIds.forEach(sId => quoteTreatments.push({ procedimiento: 'Tratamiento de historia', cantidad: 1, precio: 0, total_final: 0 }));
                                }
                            }
                        }
                        
                        if (quoteTreatments.length === 0) {
                            quoteTreatments.push({
                                procedimiento: 'Tratamiento recuperado',
                                cantidad: 1,
                                precio: 0,
                                total_final: 0
                            });
                        }

                        // Remove duplicates in quoteTreatments (if any)
                        const uniqueTreatments = Array.from(new Set(quoteTreatments.map(a => a.procedimiento)))
                            .map(proc => quoteTreatments.find(a => a.procedimiento === proc));

                        // Get the patient ID properly
                        const patientId = typeof record.patient === 'object' ? record.patient.id : record.patient;

                        const recoveredQuote = {
                            patient: patientId,
                            reason_for_consultation: 'Cotización / Presupuesto (Completado)',
                            odontogram_data: record.odontogram_data || {},
                            treatments: uniqueTreatments
                        };

                        await api.post('/api/clinical-records/', recoveredQuote);
                        recoveredPatients.add(patientId);
                        recoveredCount++;
                    }
                }
            }
            alert(`Se recuperaron ${recoveredCount} cotizaciones antiguas.`);
            loadQuotes();
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            alert('Error al recuperar cotizaciones. Detalles: ' + errorMsg);
        }
        setRecovering(false);
    };

    const handleDelete = async (id) => {
        setDeleteConfirm({ show: true, id });
    };

    const confirmDelete = async () => {
        const id = deleteConfirm.id;
        setDeleteConfirm({ show: false, id: null });
        if (id) {
            try {
                if (!navigator.onLine) {
                    savePendingTransaction('DELETE_QUOTE', { quoteId: id });
                    setSaveModal({ show: true, message: 'No hay conexión a internet, la eliminación se sincronizará luego.', type: 'offline' });
                } else {
                    await api.delete(`/api/clinical-records/${id}/`);
                    setSaveModal({ show: true, message: 'Cotización eliminada correctamente.', type: 'online' });
                    loadQuotes();
                }
                if (currentQuote && currentQuote.id === id) {
                    handleNewQuote();
                }
            } catch (error) {
                console.error('Error al eliminar:', error);
                const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
                setSaveModal({ show: true, message: `Error al eliminar. ${errorMsg}`, type: 'error' });
            }
        }
    };

    const handleChangeForm = (e) => {
        let val = e.target.value;
        if (e.target.name === 'nombre' || e.target.name === 'representante') {
            val = val.toUpperCase();
        }
        setForm({ ...form, [e.target.name]: val });
    };

    const handleTreatmentChange = (index, field, value) => {
        const newTreatments = [...treatments];
        newTreatments[index][field] = value;
        
        // Auto calculate total_final if precio or cantidad is changed manually
        if (field === 'precio' || field === 'cantidad') {
            const precio = parseFloat(newTreatments[index].precio) || 0;
            const cantidad = parseInt(newTreatments[index].cantidad) || 1;
            newTreatments[index].total_final = precio * cantidad;
        }

        // Autocomplete price if a service is selected
        if (field === 'procedimiento') {
            const selectedService = servicios.find(s => s.name === value || `${s.name} - ${s.description}` === value);
            if (selectedService) {
                const precio = selectedService.price;
                const cantidad = parseInt(newTreatments[index].cantidad) || 1;
                newTreatments[index]['precio'] = precio;
                newTreatments[index]['procedimiento'] = selectedService.name;
                newTreatments[index]['total_final'] = precio * cantidad;
            }
        }
        
        setTreatments(newTreatments);
    };

    const addTreatmentRow = () => {
        setTreatments([...treatments, { od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    };

    const removeTreatmentRow = (index) => {
        const newTreatments = treatments.filter((_, i) => i !== index);
        setTreatments(newTreatments.length ? newTreatments : [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    };

    const subtotalCost = treatments.reduce((acc, curr) => acc + ((parseFloat(curr.precio) || 0) * (parseInt(curr.cantidad) || 1)), 0);
    const globalDiscountAmount = (subtotalCost * (parseFloat(form.discountPercentage) || 0)) / 100;
    const finalTotalCost = subtotalCost - globalDiscountAmount;
    
    // Auto calculate if the manual total differs (legacy fallback, but we mainly use discountPercentage now)
    const manualTotalCost = treatments.reduce((acc, curr) => acc + (parseFloat(curr.total_final !== undefined ? curr.total_final : ((parseFloat(curr.precio)||0)*(parseInt(curr.cantidad)||1))) || 0), 0);

    const printRef = useRef(null);

    const handlePrint = async () => {
        const html2pdf = (await import('html2pdf.js')).default;
        const input = document.getElementById("print-quote-area");
        
        const opt = {
            margin: [5, 5, 5, 5],
            filename: `Presupuesto_${form.nombre || 'Paciente'}.pdf`,
            image: { type: 'jpeg', quality: 1 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };
        
        try {
            await html2pdf().set(opt).from(input).save();
        } catch (error) {
            console.error("Error generando PDF", error);
        }
    };

    const styles = {
        container: { padding: '2rem', backgroundColor: '#f8f9fb', minHeight: '100vh', display: 'flex', gap: '2rem' },
        sidebar: { width: '300px', backgroundColor: '#fff', borderRadius: '12px', padding: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' },
        main: { flex: 1, backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
        input: { padding: '0.5rem', borderRadius: '8px', border: '1px solid #cbd5e1', width: '100%', marginBottom: '1rem' },
        buttonPrimary: { padding: '0.75rem 1.5rem', borderRadius: '8px', backgroundColor: '#002b5e', color: '#fff', border: 'none', fontWeight: '700', cursor: 'pointer' },
        buttonSuccess: { padding: '0.75rem 1.5rem', borderRadius: '8px', backgroundColor: '#10b981', color: '#fff', border: 'none', fontWeight: '700', cursor: 'pointer' },
        buttonDanger: { padding: '0.5rem 1rem', borderRadius: '8px', backgroundColor: '#ef4444', color: '#fff', border: 'none', fontWeight: '700', cursor: 'pointer' },
        printOverlay: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 10000, overflowY: 'auto' }
    };

    return (
        <div className="d-flex flex-column gap-4 p-3 p-md-4" style={{ backgroundColor: '#f8f9fb', minHeight: '100vh' }}>
            <Modal 
                isOpen={saveModal.show} 
                onClose={() => setSaveModal({ ...saveModal, show: false })} 
                title={saveModal.type === 'online' ? 'Guardado Exitoso' : saveModal.type === 'offline' ? 'Guardado Local (Sin Internet)' : 'Atención'}
                skipConfirm={true}
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <i className={`bi ${saveModal.type === 'online' ? 'bi-cloud-check-fill' : saveModal.type === 'offline' ? 'bi-hdd-fill' : 'bi-exclamation-triangle-fill'}`} style={{ fontSize: '3rem', color: saveModal.type === 'online' ? '#10b981' : saveModal.type === 'offline' ? '#f59e0b' : '#ef4444' }}></i>
                    <h4 style={{ marginTop: '15px' }}>{saveModal.message}</h4>
                    {saveModal.type === 'offline' && <p style={{ color: '#666', marginTop: '10px' }}>La información se sincronizará automáticamente en segundo plano cuando vuelva la conexión.</p>}
                    <button style={{...styles.buttonPrimary, marginTop: '20px'}} onClick={() => setSaveModal({ ...saveModal, show: false })}>Entendido</button>
                </div>
            </Modal>

            <ConfirmModal 
                isOpen={deleteConfirm.show}
                onClose={() => setDeleteConfirm({ show: false, id: null })}
                onConfirm={confirmDelete}
                title="Eliminar Cotización"
                message="¿Está seguro de eliminar esta cotización? Esta acción no se puede deshacer."
            />

            {duplicateQuoteAlert.show && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', maxWidth: '500px', width: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <h4 style={{ color: '#0f172a', fontWeight: 'bold', marginBottom: '1rem' }}><i className="bi bi-exclamation-circle text-warning me-2"></i>Paciente Existente</h4>
                        <p style={{ color: '#334155' }}>Esta es una consulta de un paciente que ya ha consultado antes (<strong>{duplicateQuoteAlert.existingQuote?.form?.nombre}</strong>).</p>
                        <p style={{ color: '#334155' }}>¿Deseas actualizar la consulta que ya está, o crear una nueva separada?</p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '2rem' }}>
                            <button className="btn btn-light" onClick={() => setDuplicateQuoteAlert({ show: false, existingQuote: null, pendingSaveData: null })}>Cancelar</button>
                            <button className="btn btn-primary" onClick={() => {
                                const q = duplicateQuoteAlert.existingQuote;
                                setCurrentQuote(q);
                                setDuplicateQuoteAlert({ show: false, existingQuote: null, pendingSaveData: null });
                                executeSave(duplicateQuoteAlert.pendingSaveData.patientData, duplicateQuoteAlert.pendingSaveData.recordData, q.id, q.db_patient_id);
                            }}>Actualizar existente</button>
                            <button className="btn btn-success" onClick={() => {
                                const q = duplicateQuoteAlert.existingQuote;
                                setDuplicateQuoteAlert({ show: false, existingQuote: null, pendingSaveData: null });
                                executeSave(duplicateQuoteAlert.pendingSaveData.patientData, duplicateQuoteAlert.pendingSaveData.recordData, null, q.db_patient_id);
                            }}>Crear nueva</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for All Quotes */}
            {showAllQuotesModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '2px solid #f1f5f9', paddingBottom: '1rem' }}>
                            <h3 style={{ margin: 0, color: '#0f172a', fontWeight: 'bold' }}><i className="bi bi-list-check me-2 text-primary"></i>Listado de Todas las Cotizaciones</h3>
                            <button onClick={() => setShowAllQuotesModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>&times;</button>
                        </div>
                        <div style={{ position: 'relative', marginBottom: '1rem' }}>
                            <i className="bi bi-search" style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
                            <input 
                                type="text" 
                                placeholder="Buscar paciente por nombre o cédula..." 
                                value={searchAllQuotes}
                                onChange={(e) => setSearchAllQuotes(e.target.value)}
                                className="form-control"
                                style={{ paddingLeft: '40px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            />
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            <table className="table table-hover align-middle">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Paciente</th>
                                        <th>Cédula</th>
                                        <th>Teléfono</th>
                                        <th>Inversión</th>
                                        <th>Estado</th>
                                        <th className="text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quotes.filter(q => 
                                        (q.form.nombre || '').toLowerCase().includes(searchAllQuotes.toLowerCase()) || 
                                        (q.form.cedula || '').includes(searchAllQuotes)
                                    ).map(q => (
                                        <tr key={q.id}>
                                            <td>{q.date}</td>
                                            <td style={{ fontWeight: 'bold' }}>{q.form.nombre || 'Sin nombre'}</td>
                                            <td>{q.form.cedula || '-'}</td>
                                            <td>{q.form.telefono || '-'}</td>
                                            <td style={{ fontWeight: 'bold', color: '#002b5e' }}>
                                                {q.form.inversion ? (q.form.inversion.toString().startsWith('$') ? q.form.inversion : `$ ${q.form.inversion}`) : '-'}
                                            </td>
                                            <td>
                                                <span style={{
                                                    fontSize: '0.75rem',
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontWeight: 'bold',
                                                    backgroundColor: q.status === 'Completada' ? '#d1fae5' : '#fef3c7',
                                                    color: q.status === 'Completada' ? '#065f46' : '#92400e',
                                                    border: `1px solid ${q.status === 'Completada' ? '#a7f3d0' : '#fde68a'}`
                                                }}>
                                                    {q.status}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <button 
                                                    className="btn btn-sm btn-primary rounded-pill" 
                                                    onClick={() => {
                                                        handleSelectQuote(q);
                                                        setShowAllQuotesModal(false);
                                                    }}
                                                >
                                                    <i className="bi bi-box-arrow-up-right me-1"></i> Abrir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {quotes.filter(q => (q.form.nombre || '').toLowerCase().includes(searchAllQuotes.toLowerCase()) || (q.form.cedula || '').includes(searchAllQuotes)).length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted py-4">No hay cotizaciones registradas en el sistema.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar Historial */}
            <div className="d-print-none sidebar-cotizaciones" style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#0f172a' }}>Cotizaciones</h3>
                <div style={{ display: 'flex', gap: '5px', marginBottom: '1rem', width: '100%' }}>
                    <button style={{ ...styles.buttonSuccess, flex: 1, padding: '8px' }} onClick={handleNewQuote}>
                        + Nueva
                    </button>
                    <button 
                        onClick={() => setShowAllQuotesModal(true)}
                        style={{ flex: 1, padding: '8px', backgroundColor: '#6366f1', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                        title="Ver listado completo de cotizaciones"
                    >
                        Ver Todas
                    </button>
                    <button 
                        onClick={handleRecoverQuotes}
                        disabled={recovering}
                        style={{ flex: 1, padding: '8px', backgroundColor: '#eab308', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                        title="Recuperar cotizaciones antiguas"
                    >
                        {recovering ? '...' : 'Recuperar'}
                    </button>
                </div>
                <div style={{ position: 'relative', marginBottom: '1rem' }}>
                    <i className="bi bi-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
                    <input 
                        type="text" 
                        placeholder="Buscar por nombre o fecha..." 
                        value={searchQuote}
                        onChange={(e) => setSearchQuote(e.target.value)}
                        style={{ ...styles.input, marginBottom: 0, paddingLeft: '35px', paddingRight: '10px' }}
                    />
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {quotes.filter(q => (q.form.nombre || '').toLowerCase().includes(searchQuote.toLowerCase()) || (q.date || '').includes(searchQuote)).map(q => (
                        <div key={q.id} 
                            style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', backgroundColor: currentQuote?.id === q.id ? '#f1f5f9' : 'transparent', borderRadius: '8px', position: 'relative' }}
                            onClick={() => handleSelectQuote(q)}
                        >
                            <div style={{ fontWeight: 'bold', fontSize: '0.9rem', paddingRight: '70px' }}>{q.form.nombre || 'Sin nombre'}</div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                {q.date} {q.form.inversion ? `• Inversión: ${q.form.inversion.toString().startsWith('$') ? q.form.inversion : `$ ${q.form.inversion}`}` : ''}
                            </div>
                            <span style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                fontSize: '0.65rem',
                                padding: '2px 6px',
                                borderRadius: '12px',
                                fontWeight: 'bold',
                                backgroundColor: q.status === 'Completada' ? '#d1fae5' : '#fef3c7',
                                color: q.status === 'Completada' ? '#065f46' : '#92400e',
                                border: `1px solid ${q.status === 'Completada' ? '#a7f3d0' : '#fde68a'}`
                            }}>
                                {q.status}
                            </span>
                        </div>
                    ))}
                    {quotes.length === 0 && <p style={{ color: '#94a3b8', fontSize: '0.9rem', textAlign: 'center', marginTop: '2rem' }}>No hay cotizaciones guardadas.</p>}
                </div>
            </div>

            {/* Main Editor */}
            <div className="flex-grow-1" style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="d-print-none" style={{ width: '100%', padding: '1rem', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'space-between', backgroundColor: '#f8f9fa' }}>
                    <h2 style={{ margin: 0, color: '#002b5e' }}>Generador de Presupuestos</h2>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button style={styles.buttonPrimary} onClick={handleSaveCurrent}>
                            <i className="bi bi-save me-2"></i> Guardar
                        </button>
                        <button style={styles.buttonSuccess} onClick={handlePrint}>
                            <i className="bi bi-printer me-2"></i> Imprimir / PDF
                        </button>
                        <a 
                            href={`/clinica/pacientes${currentQuote ? `?quote_id=${currentQuote.id}&patient_id=${currentQuote.db_patient_id || ''}` : ''}`}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ ...styles.buttonPrimary, backgroundColor: '#3b82f6', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                            title="Abre la ficha clínica del paciente para completar el historial médico detallado."
                        >
                            <i className="bi bi-person-lines-fill me-2"></i> Completar Ficha
                        </a>
                        {currentQuote && (
                            <button style={styles.buttonDanger} onClick={() => handleDelete(currentQuote.id)}>
                                <i className="bi bi-trash"></i>
                            </button>
                        )}
                    </div>
                </div>

            <style>
                {`
                    .sidebar-cotizaciones { width: 100%; max-height: 250px; }
                    
                    @media screen and (max-width: 850px) {
                        #print-quote-area {
                            transform: scale(0.9);
                            transform-origin: top center;
                            margin-bottom: -10%;
                        }
                    }
                    
                    @media screen and (max-width: 768px) {
                        #print-quote-area {
                            transform: scale(0.82);
                            transform-origin: top center;
                            margin-bottom: -18%;
                        }
                    }

                    @media print {
                        body * { visibility: hidden; }
                        #print-quote-area, #print-quote-area * { visibility: visible !important; }
                        #print-quote-area { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important; }
                    }
                    .pill-table { width: 100%; border-collapse: separate; border-spacing: 0 4px; font-size: 11px; text-align: center; }
                    .pill-table th { padding: 3px; font-weight: 900; }
                    .pill-table td { background-color: #dbeafe; padding: 2px; height: 22px; border-left: 2px solid white; border-right: 2px solid white; }
                    .pill-table td:first-child { border-top-left-radius: 20px; border-bottom-left-radius: 20px; border-left: none; }
                    .pill-table td:last-child { border-top-right-radius: 20px; border-bottom-right-radius: 20px; border-right: none; }
                    .transparent-input { background: transparent; border: none; width: 100%; outline: none; font-weight: bold; color: #000; }
                `}
            </style>

            <div id="print-quote-area" ref={printRef} style={{ width: '210mm', minHeight: '270mm', backgroundColor: 'white', margin: '20px auto', padding: '10mm', boxSizing: 'border-box', position: 'relative', fontFamily: "'Segoe UI', Roboto, sans-serif", boxShadow: '0 4px 15px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column' }}>
                
                {/* Header Logo */}
                <div style={{ textAlign: 'center', marginBottom: '5px' }}>
                    <img src="/biodentality.png" alt="Biodentality" style={{ maxHeight: '60px', objectFit: 'contain' }} />
                </div>

                {/* Title */}
                <div style={{ backgroundColor: '#e5e7eb', borderRadius: '40px', padding: '8px', textAlign: 'center', marginBottom: '10px' }}>
                    <h2 style={{ margin: 0, color: '#000', fontWeight: '900', fontSize: '18px' }}>PRESUPUESTO DE TRATAMIENTO ODONTOLÓGICO</h2>
                </div>

                {/* Form Fields */}
                <div style={{ border: '2px solid #93c5fd', borderRadius: '15px', padding: '10px 15px', marginBottom: '10px', fontSize: '12px', fontWeight: 'bold' }}>
                    <div style={{ display: 'flex', gap: '15px', marginBottom: '8px' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '150px' }}>Nombre del paciente:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="nombre" value={form.nombre} onChange={handleChangeForm} className="transparent-input" />
                            </div>
                        </div>
                        <div style={{ width: '250px', display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '60px' }}>Cédula:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="cedula" value={form.cedula} onChange={handleChangeForm} className="transparent-input" maxLength="50" />
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '15px', marginBottom: '8px' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '150px' }}>Nombre Rep.:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="representante" value={form.representante} onChange={handleChangeForm} className="transparent-input" />
                            </div>
                        </div>
                        <div style={{ width: '250px', display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '140px' }}>Fecha de nacimiento:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="fechaNacimiento" value={form.fechaNacimiento} onChange={handleChangeForm} type="date" className="transparent-input" />
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '15px', marginBottom: '8px' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '150px' }}>C.I. Rep.:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="cedulaRepresentante" value={form.cedulaRepresentante} onChange={handleChangeForm} className="transparent-input" maxLength="50" />
                            </div>
                        </div>
                        <div style={{ width: '250px', display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '70px' }}>Teléfono:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="telefono" value={form.telefono} onChange={handleChangeForm} className="transparent-input" maxLength="50" />
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '15px', marginBottom: '8px' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '150px' }}>Dirección:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="direccion" value={form.direccion} onChange={handleChangeForm} className="transparent-input" />
                            </div>
                        </div>
                        <div style={{ width: '250px', display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '80px' }}>Ocupación:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="ocupacion" value={form.ocupacion} onChange={handleChangeForm} className="transparent-input" />
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '15px' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '150px' }}>Correo:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="correo" value={form.correo} onChange={handleChangeForm} className="transparent-input" />
                            </div>
                        </div>
                        <div style={{ width: '250px', display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '80px' }}>Inversión:</span>
                            <div style={{ flex: 1, backgroundColor: '#dbeafe', borderRadius: '20px', padding: '4px 15px', minHeight: '26px', display: 'flex', alignItems: 'center' }}>
                                <input name="inversion" value={form.inversion || ''} onChange={handleChangeForm} className="transparent-input" placeholder="$ 0.00" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Odontograma */}
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', height: '320px' }}>
                    <div style={{ transform: 'scale(0.70)', transformOrigin: 'top center', minWidth: '950px', display: 'flex', justifyContent: 'center' }}>
                        <Odontograma data={odontogramaData} onChange={setOdontogramaData} shape="circle" />
                    </div>
                </div>



                {/* Table */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '900' }}>PLAN DE TRATAMIENTO</h3>
                    <div className="d-print-none">
                        <button className="btn btn-sm btn-outline-primary me-2" onClick={addTreatmentRow} title="Añadir Fila">+</button>
                    </div>
                </div>
                <table className="pill-table">
                    <thead>
                        <tr>
                            <th style={{ width: '10%' }}>N°</th>
                            <th style={{ width: '40%' }}>PROCEDIMIENTO</th>
                            <th style={{ width: '15%' }}>CANTIDAD</th>
                            <th style={{ width: '15%' }}>PRECIO UNITARIO</th>
                            <th style={{ width: '20%', position: 'relative' }}>
                                TOTAL
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {treatments.map((t, i) => (
                            <tr key={i} style={{ position: 'relative' }}>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontWeight: 'bold' }}>
                                        {i + 1}
                                    </div>
                                </td>
                                <td>
                                    <TermAutocomplete
                                        value={t.procedimiento}
                                        termsList={servicios.map(s => ({ code: s.name, description: s.description, price: s.price }))}
                                        onUpdate={(val) => handleTreatmentChange(i, 'procedimiento', val)}
                                        onSelectFullItem={(item) => {
                                            const newTreatments = [...treatments];
                                            newTreatments[i]['procedimiento'] = item.code;
                                            newTreatments[i]['precio'] = item.price;
                                            newTreatments[i]['total_final'] = item.price * (parseInt(newTreatments[i].cantidad) || 1);
                                            setTreatments(newTreatments);
                                        }}
                                        insertField="code"
                                        placeholder="Buscar servicio..."
                                        openUp={true}
                                        style={{ backgroundColor: 'transparent', border: 'none', textAlign: 'center', width: '100%', outline: 'none' }}
                                    />
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <input type="number" min="1" value={t.cantidad !== undefined && t.cantidad !== '' ? t.cantidad : ''} onChange={e => handleTreatmentChange(i, 'cantidad', e.target.value)} onBlur={e => { if(e.target.value === '') handleTreatmentChange(i, 'cantidad', 1) }} className="transparent-input text-center" style={{ width: '100%', backgroundColor: 'transparent', border: 'none', outline: 'none', boxShadow: 'none' }} />
                                    </div>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                        <span style={{marginRight: '5px'}}>$</span> <input type="number" min="0" step="0.01" value={t.precio} onChange={e => handleTreatmentChange(i, 'precio', e.target.value)} className="transparent-input text-center" style={{ width: '80px', backgroundColor: 'transparent', border: 'none', outline: 'none', boxShadow: 'none' }} />
                                    </div>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%', height: '100%' }}>
                                        <span style={{marginRight: '5px'}}>$</span> <input type="number" min="0" step="0.01" value={t.total_final !== undefined ? t.total_final : ((parseFloat(t.precio) || 0) * (parseInt(t.cantidad) || 1))} onChange={e => handleTreatmentChange(i, 'total_final', e.target.value)} className="transparent-input text-center" style={{ width: '80px', backgroundColor: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', fontWeight: 'bold' }} />
                                        <button className="btn btn-sm text-danger d-print-none" style={{ position: 'absolute', right: '-20px', padding: '0 5px' }} onClick={() => removeTreatmentRow(i)}>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {/* Rellenar filas vacías para diseño */}
                        {[...Array(Math.max(0, 4 - treatments.length))].map((_, i) => (
                            <tr key={`empty-${i}`}>
                                <td></td><td></td><td></td><td></td><td></td>
                            </tr>
                        ))}
                        <tr style={{ backgroundColor: 'transparent' }}>
                            <td colSpan="3" style={{ backgroundColor: 'transparent', paddingRight: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <div style={{ width: '200px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', fontWeight: '900', fontSize: '14px' }}>
                                        <span>SUBTOTAL</span>
                                    </div>
                                </div>
                            </td>
                            <td colSpan="2" style={{ fontWeight: '900', fontSize: '14px', backgroundColor: 'transparent', textAlign: 'center' }}>${subtotalCost.toFixed(2)}</td>
                        </tr>
                        
                        <tr style={{ backgroundColor: 'transparent' }}>
                            <td colSpan="3" style={{ backgroundColor: 'transparent', paddingRight: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <div style={{ width: '200px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: '900', fontSize: '14px' }}>
                                        <span>DESCUENTO</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <input 
                                                type="number" 
                                                min="0" 
                                                max="100" 
                                                value={form.discountPercentage || 0} 
                                                onChange={(e) => setForm({...form, discountPercentage: e.target.value})} 
                                                className="transparent-input text-center d-print-none"
                                                style={{ width: '40px', backgroundColor: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', fontWeight: 'bold', borderBottom: '1px solid #ccc', padding: '0' }} 
                                            />
                                            <span className="d-print-none">%</span>
                                            {form.discountPercentage > 0 && <span className="d-none d-print-inline">{form.discountPercentage}%</span>}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td colSpan="2" style={{ fontWeight: '900', fontSize: '14px', backgroundColor: 'transparent', textAlign: 'center' }}>
                                ${globalDiscountAmount.toFixed(2)}
                            </td>
                        </tr>
                        
                        <tr style={{ backgroundColor: 'transparent' }}>
                            <td colSpan="3" style={{ backgroundColor: 'transparent', paddingRight: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <div style={{ width: '200px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', fontWeight: '900', fontSize: '14px' }}>
                                        <span>TOTAL A PAGAR</span>
                                    </div>
                                </div>
                            </td>
                            <td colSpan="2" style={{ fontWeight: '900', fontSize: '14px', backgroundColor: 'transparent', textAlign: 'center' }}>${finalTotalCost.toFixed(2)}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Footer */}
                <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'center', backgroundColor: '#e5e7eb', borderRadius: '30px', padding: '8px 20px', alignItems: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', display: 'flex', gap: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#16a34a' }}>
                            <i className="bi bi-whatsapp"></i> CONTÁCTANOS: 0991362188
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#002b5e' }}>
                            <i className="bi bi-geo-alt-fill"></i> DIRECCIÓN: SUCRE Y VENEZUELA ESQUINA (CARCHI-TULCÁN)
                        </div>
                    </div>
                </div>

            </div>
        </div>
        </div>
    );
};

export default Consultas;
